/**
 * /en/admin/structure/paragraphs_type/vuc_status_box/fields
 */
import CLASS_NAMES from "../constants/css-constants.es6.js";
import {ATTRIBUTES} from "../constants/html-constants.es6.js";
import { SESSION_STORAGE_KEYS } from "../constants/js-constants.es6.js";
import { VIEW_MODES } from "../constants/drupal-constants.es6.js";
import LinkField from "../core/link-field.es6.js";
import Element from "../core/element.es6.js";
import Field from "../core/field.es6.js";
import Paragraph from "../core/paragraph.es6.js";
import SessionStorageUtils from "../utils/session-storage-utils.es6.js";

export const VIRTUAL_URGENT_CARE_STATUS_BOX_REFRESH_INTERVAL_SECONDS = 30;

export default class VirtualUrgentCareStatusBoxParagraph extends Paragraph {
  #luminisServiceUtils;

  #dataRefreshMilliseconds;

  constructor({
    luminisServiceUtils,
    parentElement,
    dataRefreshSeconds = VIRTUAL_URGENT_CARE_STATUS_BOX_REFRESH_INTERVAL_SECONDS,
  }) {
    super({
      machineName: 'vuc_status_box',
      parentElement,
    });

    this.#luminisServiceUtils = luminisServiceUtils;

    const MILLISECONDS_IN_SECOND = 1000;
    this.#dataRefreshMilliseconds = dataRefreshSeconds * MILLISECONDS_IN_SECOND;
  }

  get currentPositionConcatenator() {
    return new Field({
      machineName: 'field_position_concatenator',
      parentElement: this,
    });
  }

  get estimatedWaitTimeHours() {
    return new Element({
      parentElement: this,
      selector: '.vuc-estimated-wait-time-hours',
    });
  }

  get estimatedWaitTimeHourLabel() {
    return new Field({
      machineName: 'field_estimated_wait_hour',
      parentElement: this,
    });
  }

  get estimatedWaitTimeHoursLabel() {
    return new Field({
      machineName: 'field_estimated_wait_hours',
      parentElement: this,
    });
  }

  get estimatedWaitTimeMinutes() {
    return new Element({
      parentElement: this,
      selector: '.vuc-estimated-wait-time-minutes',
    });
  }

  get estimatedWaitTimeMinuteLabel() {
    return new Field({
      machineName: 'field_estimated_wait_minute',
      parentElement: this,
    });
  }

  get estimatedWaitTimeMinutesLabel() {
    return new Field({
      machineName: 'field_estimated_wait_minutes',
      parentElement: this,
    });
  }

  get launchVisitButton() {
    return new LinkField({
      machineName: 'field_seafoam_button',
      parentElement: this,
    });
  }

  get lessThanLabel() {
    return new Field({
      machineName: 'field_less_than_label',
      parentElement: this,
    });
  }

  get patientCount() {
    return new Element({
      parentElement: this,
      selector: '.vuc-patient-count',
    });
  }

  get patientCountSuffixPlural() {
    return new Field({
      machineName: 'field_patient_count_plural',
      parentElement: this,
    });
  }

  get patientCountSuffixSingular() {
    return new Field({
      machineName: 'field_patient_count_singular',
      parentElement: this,
    });
  }

  get positionIndex() {
    return new Element({
      parentElement: this,
      selector: '.vuc-position-index',
    });
  }

  get positionLength() {
    return new Element({
      parentElement: this,
      selector: '.vuc-position-length',
    });
  }

  async initialize() {
    const isLaunchVisitViewMode =
      this.viewMode?.toUpperCase() ===
      VIEW_MODES.VIRTUAL_URGENT_CARE_LAUNCH_VISIT.toUpperCase();

    if (isLaunchVisitViewMode) {
      await this.#updateJoinLink();
    } else {
      await this.#updateMetrics();
      setInterval(() => this.#updateMetrics(), this.#dataRefreshMilliseconds);
    }
  }

  async #updateJoinLink() {
    const virtualUrgentCareSessionId = SessionStorageUtils.getItem(
      SESSION_STORAGE_KEYS.VIRTUAL_URGENT_CARE_SESSION_ID,
    );

    if (virtualUrgentCareSessionId) {
      const virtualUrgentCareSession = await this.#luminisServiceUtils.getVirtualUrgentCareSession(
        virtualUrgentCareSessionId,
      );

      if (virtualUrgentCareSession) {
        this.#updateLaunchVisitButton(
          virtualUrgentCareSession.appointment.joinLink,
        );
      }
    }
  }

  async #updateMetrics() {
    switch (this.viewMode) {
      case VIEW_MODES.VIRTUAL_URGENT_CARE_IN_QUEUE: {
        const virtualUrgentCareSessionId = SessionStorageUtils.getItem(
          SESSION_STORAGE_KEYS.VIRTUAL_URGENT_CARE_SESSION_ID,
        );

        if (virtualUrgentCareSessionId) {
          const virtualUrgentCareSession = await this.#luminisServiceUtils.getVirtualUrgentCareSession(
            virtualUrgentCareSessionId,
          );

          if (virtualUrgentCareSession) {
            this.#updateEstimatedWaitTime(
              virtualUrgentCareSession.estimatedWaitTimeSeconds,
            );

            this.#updatePositionIndex(virtualUrgentCareSession.position.index);

            this.currentPositionConcatenator.$().css('visibility', 'visible');

            this.#updatePositionLength(
              virtualUrgentCareSession.position.length,
            );
          }
        }

        break;
      }
      default: {
        const estimatedWaitTimeSeconds = await this.#luminisServiceUtils.getVirtualUrgentCareEstimatedWaitTime();
        const patientCount = await this.#luminisServiceUtils.getVirtualUrgentCarePatientCount();

        this.#updateEstimatedWaitTime(estimatedWaitTimeSeconds);
        this.#updatePatientCount(patientCount);
        break;
      }
    }
  }

  #updateEstimatedWaitTime(estimatedWaitTimeSeconds) {
    if (estimatedWaitTimeSeconds < 60) {
      this.lessThanLabel.$().removeClass(CLASS_NAMES.HIDDEN);
      this.#displayMinutes(1);
      return;
    }

    this.lessThanLabel.$().addClass(CLASS_NAMES.HIDDEN);

    const SECONDS_IN_MINUTE = 60;
    const MINUTES_IN_HOUR = 60;
    const secondsInHour = SECONDS_IN_MINUTE * MINUTES_IN_HOUR;

    let displayHours = Math.floor(estimatedWaitTimeSeconds / secondsInHour);

    let displayMinutes = Math.ceil(
      (estimatedWaitTimeSeconds - displayHours * secondsInHour) /
        SECONDS_IN_MINUTE,
    );

    if (displayMinutes === 60) {
      displayHours += 1;
      displayMinutes = 0;
    }

    this.#displayHours(displayHours);
    this.#displayMinutes(displayMinutes);
  }

  #displayHours(hours) {
    VirtualUrgentCareStatusBoxParagraph.#displayTimeSegment(
      hours,
      this.estimatedWaitTimeHours,
      this.estimatedWaitTimeHoursLabel,
      this.estimatedWaitTimeHourLabel,
    );
  }

  #displayMinutes(minutes) {
    VirtualUrgentCareStatusBoxParagraph.#displayTimeSegment(
      minutes,
      this.estimatedWaitTimeMinutes,
      this.estimatedWaitTimeMinutesLabel,
      this.estimatedWaitTimeMinuteLabel,
    );
  }

  static #displayTimeSegment(
    number,
    numberField,
    pluralSuffixField,
    singularSuffixField,
  ) {
    numberField.$().text(number);

    const showField = field => field.$().removeClass(CLASS_NAMES.HIDDEN);
    const hideField = field => field.$().addClass(CLASS_NAMES.HIDDEN);

    if (number === 0) {
      hideField(numberField);
      hideField(pluralSuffixField);
      hideField(singularSuffixField);
    } else if (number === 1) {
      showField(numberField);
      hideField(pluralSuffixField);
      showField(singularSuffixField);
    } else {
      showField(numberField);
      showField(pluralSuffixField);
      hideField(singularSuffixField);
    }
  }

  #updatePatientCount(patientCount) {
    this.patientCount.$().text(patientCount);

    const showField = field => field.$().removeClass(CLASS_NAMES.HIDDEN);
    const hideField = field => field.$().addClass(CLASS_NAMES.HIDDEN);

    if (patientCount === 1) {
      hideField(this.patientCountSuffixPlural);
      showField(this.patientCountSuffixSingular);
    } else {
      hideField(this.patientCountSuffixSingular);
      showField(this.patientCountSuffixPlural);
    }
  }

  #updatePositionIndex(positionIndex) {
    this.positionIndex.$().text(positionIndex);
  }

  #updatePositionLength(positionLength) {
    this.positionLength.$().text(positionLength);
  }

  #updateLaunchVisitButton(url) {
    this.launchVisitButton.$().attr(ATTRIBUTES.HREF, url);
  }
}
